<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Review Appraisal</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/manager-dashboard">Manage Appraisals</router-link></li>
                    <li class="breadcrumb-item active">Review</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading appraisal...</p>
          </div>

          <!-- Review Form -->
          <div v-else-if="appraisal">

            <!-- Employee Info Card -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card border-primary">
                  <div class="card-header bg-primary text-white">
                    <h5 class="mb-0 text-white">Employee Information</h5>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3">
                        <label class="fw-bold">Employee Name:</label>
                        <p>{{ appraisal.employeeName }}</p>
                      </div>
                      <div class="col-md-3">
                        <label class="fw-bold">Position:</label>
                        <p>{{ appraisal.positionTitle }}</p>
                      </div>
                      <div class="col-md-3">
                        <label class="fw-bold">Department:</label>
                        <p>{{ appraisal.department }}</p>
                      </div>
                      <div class="col-md-3">
                        <label class="fw-bold">Review Period:</label>
                        <p>{{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section A: KPAs -->
            <div class="row mb-4" v-if="kpas.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-target me-2 text-primary"></i>
                      Section A: Key Performance Areas
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="alert alert-info">
                      <i class="mdi mdi-information me-2"></i>
                      <strong>Rating Scale:</strong> 1 = Poor, 2 = Below Average, 3 = Average, 4 = Good, 5 = Outstanding
                    </div>

                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="table-light">
                        <tr>
                          <th width="3%">#</th>
                          <th width="22%">KPA</th>
                          <th width="20%">Results Achieved</th>
                          <th width="8%">Self Rating</th>
                          <th width="7%">Weight</th>
                          <th width="12%">Your Rating</th>
                          <th width="28%">Your Comments</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(kpa) in kpas" :key="kpa.id">
                          <td class="text-center">{{ kpa.sNo }}</td>
                          <td>{{ kpa.kpa }}</td>
                          <td>{{ kpa.resultsAchieved || '-' }}</td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ kpa.selfRating || '-' }}</span>
                          </td>
                          <td class="text-center">{{ kpa.weightage }}%</td>
                          <td>
                            <select
                                v-model="kpa.appraiserRating"
                                class="form-select form-select-sm"
                                required
                            >
                              <option value="">Select</option>
                              <option value="1">1 - Poor</option>
                              <option value="2">2 - Below Avg</option>
                              <option value="3">3 - Average</option>
                              <option value="4">4 - Good</option>
                              <option value="5">5 - Outstanding</option>
                            </select>
                          </td>
                          <td>
                              <textarea
                                  v-model="kpa.appraiserComment"
                                  class="form-control form-control-sm"
                                  rows="2"
                                  placeholder="Add your comments..."
                              ></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Section A Overall Rating -->
                    <div class="row mt-3">
                      <div class="col-md-6">
                        <div class="bg-light p-3 rounded">
                          <label class="form-label fw-bold">Section A: Overall Rating (Auto-calculated)</label>
                          <input
                              type="number"
                              v-model="reviewData.sectionA_AppraiserOverallRating"
                              class="form-control bg-white"
                              min="1"
                              max="5"
                              step="0.01"
                              readonly
                          >
                          <small class="text-muted">
                            <i class="mdi mdi-calculator me-1"></i>
                            Weighted average: {{ calculatedSectionA || 'Fill in KPA ratings' }}
                          </small>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="bg-light p-3 rounded">
                          <label class="fw-bold">Employee's Self Rating:</label>
                          <h4 class="mb-0">
                            {{ appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : 'Not provided' }}
                          </h4>
                        </div>
                      </div>
                    </div>

                    <!-- Organizational Parameters -->
                    <div v-if="organizationalParams.length > 0" class="mt-4">
                      <h6 class="text-primary mb-3">Key Organizational Parameters</h6>
                      <div class="table-responsive">
                        <table class="table table-bordered table-sm">
                          <thead class="table-light">
                          <tr>
                            <th width="3%">#</th>
                            <th width="35%">Question</th>
                            <th width="10%">Employee's Answer</th>
                            <th width="12%">Your Rating</th>
                            <th width="40%">Your Comments</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="param in organizationalParams" :key="param.id">
                            <td class="text-center">{{ param.sNo }}</td>
                            <td>{{ param.question }}</td>
                            <td class="text-center">
                              <span class="badge bg-primary">{{ param.answer || '-' }}</span>
                            </td>
                            <td>
                              <select
                                  v-model="param.appraiserRating"
                                  class="form-select form-select-sm"
                              >
                                <option value="">Select</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                                <option value="5">5</option>
                              </select>
                            </td>
                            <td>
                                <textarea
                                    v-model="param.appraiserComment"
                                    class="form-control form-control-sm"
                                    rows="1"
                                    placeholder="Comments..."
                                ></textarea>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <!-- Section B: Attributes -->
            <div class="row mb-4" v-if="attributes.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-star me-2 text-primary"></i>
                      Section B: Critical Success Attributes
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="30%">Attribute</th>
                          <th width="12%">Self Rating</th>
                          <th width="15%">Your Rating</th>
                          <th width="38%">Your Comments</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="attr in attributes" :key="attr.id">
                          <td class="text-center">{{ attr.sNo }}</td>
                          <td>{{ attr.attribute }}</td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ attr.selfRating || '-' }}</span>
                          </td>
                          <td>
                            <select
                                v-model="attr.appraiserRating"
                                class="form-select form-select-sm"
                                required
                            >
                              <option value="">Select</option>
                              <option value="1">1 - Poor</option>
                              <option value="2">2 - Below Avg</option>
                              <option value="3">3 - Average</option>
                              <option value="4">4 - Good</option>
                              <option value="5">5 - Outstanding</option>
                            </select>
                          </td>
                          <td>
                              <textarea
                                  v-model="attr.appraiserComment"
                                  class="form-control form-control-sm"
                                  rows="2"
                                  placeholder="Add your comments..."
                              ></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Section B Overall Rating -->
                    <div class="row mt-3">
                      <div class="col-md-6">
                        <div class="bg-light p-3 rounded">
                          <label class="form-label fw-bold">Section B: Overall Rating (Auto-calculated)</label>
                          <input
                              type="number"
                              v-model="reviewData.sectionB_AppraiserOverallRating"
                              class="form-control bg-white"
                              min="1"
                              max="5"
                              step="0.01"
                              readonly
                          >
                          <small class="text-muted">
                            <i class="mdi mdi-calculator me-1"></i>
                            Simple average: {{ calculatedSectionB || 'Fill in attribute ratings' }}
                          </small>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="bg-light p-3 rounded">
                          <label class="fw-bold">Employee's Self Rating:</label>
                          <h4 class="mb-0">
                            {{ appraisal.sectionB_SelfOverallRating ? parseFloat(appraisal.sectionB_SelfOverallRating).toFixed(2) : 'Not provided' }}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Employee Feedback Summary (Read-only) -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-comment-text me-2 text-primary"></i>
                      Employee's Feedback Summary
                    </h5>
                  </div>
                  <div class="card-body">

                    <!-- Job Content -->
                    <div v-if="jobContent.length > 0" class="mb-3">
                      <h6 class="text-muted">Job Content</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in jobContent" :key="item.id">
                            <td width="50%">{{ item.attribute }}</td>
                            <td width="10%">
                                <span :class="['badge', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                  {{ item.response }}
                                </span>
                            </td>
                            <td width="40%">{{ item.remarks || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Growth Opportunities -->
                    <div v-if="growthOpportunities.length > 0" class="mb-3">
                      <h6 class="text-muted">Growth Opportunities</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in growthOpportunities" :key="item.id">
                            <td width="50%">{{ item.attribute }}</td>
                            <td width="10%">
                                <span :class="['badge', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                  {{ item.response }}
                                </span>
                            </td>
                            <td width="40%">{{ item.remarks || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Training Priorities -->
                    <div v-if="trainings.length > 0" class="mb-3">
                      <h6 class="text-muted">Training Priorities</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in trainings" :key="item.id">
                            <td>{{ item.trainingProgram }}</td>
                            <td width="20%" class="text-center">
                              <span class="badge bg-primary">Priority: {{ item.priority }}</span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Employee Comments -->
                    <div v-if="appraisal.associateComments">
                      <h6 class="text-muted">Employee's Additional Comments</h6>
                      <div class="bg-light p-3 rounded">
                        {{ appraisal.associateComments }}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <!-- Goals Section (Read-only) -->
            <div class="row mb-4" v-if="goals.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-flag-checkered me-2 text-primary"></i>
                      Employee's Goals for Next Period
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered table-sm">
                        <thead class="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="75%">Action Plan</th>
                          <th width="20%">Target Date</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="goal in goals" :key="goal.id">
                          <td class="text-center">{{ goal.sNo }}</td>
                          <td>{{ goal.actionPlan }}</td>
                          <td>{{ formatDate(goal.targetDate) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <router-link to="/manager-dashboard" class="btn btn-secondary">
                        <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
                      </router-link>
                      <div>
                        <button @click="saveReview" class="btn btn-primary" :disabled="submitting">
                          <span v-if="submitting">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Submitting...
                          </span>
                          <span v-else>
                            <i class="mdi mdi-check me-1"></i> Submit Review
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Not Found -->
          <div v-else class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline display-4 text-muted"></i>
            <h5 class="mt-3">Appraisal Not Found</h5>
            <p class="text-muted">The appraisal you're trying to review doesn't exist or you don't have access to it.</p>
            <router-link to="/manager-dashboard" class="btn btn-primary mt-3">
              <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
            </router-link>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "ManagerReviewForm",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      submitting: false,
      appraisalId: null,
      appraisal: null,
      kpas: [],
      organizationalParams: [],
      attributes: [],
      jobContent: [],
      growthOpportunities: [],
      workCulture: [],
      colleagues: [],
      trainings: [],
      additionalFeedback: null,
      goals: [],
      reviewData: {
        sectionA_AppraiserOverallRating: '',
        sectionB_AppraiserOverallRating: ''
      }
    };
  },

  computed: {
    // Auto-calculate Section A Overall Rating (Weighted Average)
    calculatedSectionA() {
      if (!this.kpas || this.kpas.length === 0) return null;

      let totalWeightedScore = 0;
      let totalWeight = 0;

      for (const kpa of this.kpas) {
        if (kpa.appraiserRating && kpa.weightage) {
          const rating = parseFloat(kpa.appraiserRating);
          const weight = parseFloat(kpa.weightage);
          totalWeightedScore += (rating * weight);
          totalWeight += weight;
        }
      }

      if (totalWeight === 0) return null;
      return (totalWeightedScore / totalWeight).toFixed(2);
    },

    // Auto-calculate Section B Overall Rating (Simple Average)
    calculatedSectionB() {
      if (!this.attributes || this.attributes.length === 0) return null;

      let totalRating = 0;
      let count = 0;

      for (const attr of this.attributes) {
        if (attr.appraiserRating) {
          totalRating += parseFloat(attr.appraiserRating);
          count++;
        }
      }

      if (count === 0) return null;
      return (totalRating / count).toFixed(2);
    }
  },

  watch: {
    // Auto-update Section A rating when KPAs change
    calculatedSectionA(newVal) {
      if (newVal) {
        this.reviewData.sectionA_AppraiserOverallRating = newVal;
      }
    },

    // Auto-update Section B rating when Attributes change
    calculatedSectionB(newVal) {
      if (newVal) {
        this.reviewData.sectionB_AppraiserOverallRating = newVal;
      }
    }
  },

  mounted() {
    this.appraisalId = this.$route.query.id;
    if (this.appraisalId) {
      this.loadAppraisal();
    }
  },

  methods: {
    async loadAppraisal() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAppraisalForReview");
        data.append("appraisalId", this.appraisalId);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const details = res.data.data;

          this.appraisal = details.appraisal;
          this.kpas = details.kpas || [];
          this.organizationalParams = details.organizationalParams || [];
          this.attributes = details.attributes || [];
          this.jobContent = details.jobContent || [];
          this.growthOpportunities = details.growthOpportunities || [];
          this.workCulture = details.workCulture || [];
          this.colleagues = details.colleagues || [];
          this.trainings = details.trainings || [];
          this.additionalFeedback = details.additionalFeedback;
          this.goals = details.goals || [];
        } else {
          this.appraisal = null;
          this.$toast?.error("Failed to load appraisal") || alert("Failed to load appraisal");
        }
      } catch (error) {
        console.error("Failed to load appraisal:", error);
        this.$toast?.error("Error loading appraisal") || alert("Error loading appraisal");
      } finally {
        this.loading = false;
      }
    },

    async saveReview() {
      // Validation
      if (!this.reviewData.sectionA_AppraiserOverallRating) {
        this.$toast?.error("Please provide Section A overall rating") || alert("Please provide Section A overall rating");
        return;
      }

      if (!this.reviewData.sectionB_AppraiserOverallRating) {
        this.$toast?.error("Please provide Section B overall rating") || alert("Please provide Section B overall rating");
        return;
      }

      // Check if all KPAs are rated
      const unratedKPAs = this.kpas.filter(k => !k.appraiserRating);
      if (unratedKPAs.length > 0) {
        this.$toast?.error("Please rate all KPAs") || alert("Please rate all KPAs");
        return;
      }

      // Check if all attributes are rated
      const unratedAttrs = this.attributes.filter(a => !a.appraiserRating);
      if (unratedAttrs.length > 0) {
        this.$toast?.error("Please rate all attributes") || alert("Please rate all attributes");
        return;
      }

      this.submitting = true;

      try {
        const reviewData = {
          kpas: this.kpas.map(k => ({
            id: k.id,
            appraiserRating: k.appraiserRating,
            appraiserComment: k.appraiserComment
          })),
          organizationalParams: this.organizationalParams.map(p => ({
            id: p.id,
            appraiserRating: p.appraiserRating,
            appraiserComment: p.appraiserComment
          })),
          attributes: this.attributes.map(a => ({
            id: a.id,
            appraiserRating: a.appraiserRating,
            appraiserComment: a.appraiserComment
          })),
          sectionA_AppraiserOverallRating: this.reviewData.sectionA_AppraiserOverallRating,
          sectionB_AppraiserOverallRating: this.reviewData.sectionB_AppraiserOverallRating
        };

        const data = new FormData();
        data.append("function", "saveManagerReview");
        data.append("appraisalId", this.appraisalId);
        data.append("reviewData", JSON.stringify(reviewData));

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Review submitted successfully!") || alert("Review submitted successfully!");
          this.$router.push('/manager-dashboard');
        } else {
          this.$toast?.error(res.data?.message || "Failed to submit review") || alert("Failed to submit review");
        }
      } catch (error) {
        console.error("Failed to submit review:", error);
        this.$toast?.error("Error submitting review") || alert("Error submitting review");
      } finally {
        this.submitting = false;
      }
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.form-control-sm, .form-select-sm {
  font-size: 0.875rem;
}

.table-sm > :not(caption) > * > * {
  padding: 0.5rem 0.5rem;
}

.badge {
  font-size: 0.875rem;
}
</style>